import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import Input from '../../../components/Form/Input';
import Select from '../../../components/Form/Select';
import { InTransactionFormData, DropdownOption } from './inTransactionTypes';

interface InAmountSectionProps {
  register: UseFormRegister<InTransactionFormData>;
  errors: FieldErrors<InTransactionFormData>;
  currencyOptions: DropdownOption[];
  accountOptions: DropdownOption[];
  selectedCurrencyRaw: string;
  selectedCurrencyCode: string;
  selectedCollectedCurrencyRaw: string;
  selectedCollectedCurrencyCode: string;
  /** Currency code of the linked invoice, e.g. "USD" (empty when no invoice) */
  invoiceCurrencyCode?: string;
  amountValue?: number;
  exchangeRateValue?: number;
}

const InAmountSection: React.FC<InAmountSectionProps> = ({
  register,
  errors,
  currencyOptions,
  accountOptions,
  selectedCurrencyRaw,
  selectedCurrencyCode,
  selectedCollectedCurrencyRaw,
  selectedCollectedCurrencyCode,
  invoiceCurrencyCode = '',
  amountValue = 0,
  exchangeRateValue = 0,
}) => {
  // Rate always shows for non-EGP payments; it also shows when paying an
  // EGP amount against a foreign-currency invoice (EGP ÷ rate = invoice currency)
  const egpAgainstForeignInvoice =
    selectedCurrencyCode === 'EGP' && !!invoiceCurrencyCode && invoiceCurrencyCode !== 'EGP';
  const showRate = !!selectedCurrencyRaw && (selectedCurrencyCode !== 'EGP' || egpAgainstForeignInvoice);
  const rateCurrency = selectedCurrencyCode !== 'EGP' ? selectedCurrencyCode : invoiceCurrencyCode;

  const conversionApplies =
    !!invoiceCurrencyCode && !!selectedCurrencyCode &&
    invoiceCurrencyCode !== selectedCurrencyCode &&
    (amountValue || 0) > 0 && (exchangeRateValue || 0) > 0;
  const appliedToInvoice = !conversionApplies ? 0
    : selectedCurrencyCode === 'EGP' ? (amountValue / exchangeRateValue)
    : invoiceCurrencyCode === 'EGP' ? (amountValue * exchangeRateValue)
    : amountValue;
  return (
    <div className="bg-gray-50 rounded-xl p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
        <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Amount
      </h3>
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Input
            label="Amount"
            type="number"
            step="0.01"
            required
            {...register('amount', {
              required: 'Amount is required',
              valueAsNumber: true,
              min: { value: 0.01, message: 'Amount must be positive' }
            })}
            error={errors.amount?.message}
            placeholder="0.00"
          />
          <Select
            label="Currency"
            {...register('currency', { required: 'Currency is required' })}
            options={currencyOptions}
            error={errors.currency?.message}
          />
          {showRate && (
            <Input
              label={`Exchange Rate (${rateCurrency} to EGP)`}
              type="number"
              step="0.01"
              required
              {...register('exchangeRate', {
                valueAsNumber: true,
                min: { value: 0.01, message: 'Rate must be positive' }
              })}
              error={errors.exchangeRate?.message}
              placeholder="e.g., 50.00"
              helperText={egpAgainstForeignInvoice
                ? `EGP amount ÷ rate = ${invoiceCurrencyCode} applied to the invoice`
                : undefined}
            />
          )}
        </div>

        {conversionApplies && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              Applied to the invoice:{' '}
              <strong>
                {appliedToInvoice.toLocaleString(undefined, { maximumFractionDigits: 2 })} {invoiceCurrencyCode}
              </strong>
              {' '}
              ({selectedCurrencyCode === 'EGP'
                ? `${Number(amountValue).toLocaleString()} EGP ÷ ${exchangeRateValue}`
                : `${Number(amountValue).toLocaleString()} ${selectedCurrencyCode} × ${exchangeRateValue}`})
            </p>
          </div>
        )}

        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Cheque / Bank Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Input
              label="Cheque Date"
              type="date"
              {...register('chequeDate')}
              error={errors.chequeDate?.message}
            />
            <Input
              label="Cheque Number"
              type="text"
              {...register('chequeNumber')}
              error={errors.chequeNumber?.message}
              placeholder="Enter cheque number"
            />
            <Input
              label="Bank Name"
              type="text"
              {...register('bankName')}
              error={errors.bankName?.message}
              placeholder="Issuing bank name"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            <Select
              label="Money In Account"
              {...register('depositAccount')}
              options={[{ value: '', label: 'Select account' }, ...accountOptions]}
              error={errors.depositAccount?.message}
              helperText="Account where the money will be deposited"
            />
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Amount Collected In (if different currency)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Select
              label="Collected Currency"
              {...register('amountCollectedCurrency')}
              options={[{ value: '', label: 'Same as above' }, ...currencyOptions]}
              error={errors.amountCollectedCurrency?.message}
            />
            {selectedCollectedCurrencyRaw && selectedCollectedCurrencyCode !== 'EGP' && selectedCollectedCurrencyRaw !== selectedCurrencyRaw && (
              <Input
                label={`Collected Rate (${selectedCollectedCurrencyCode} to EGP)`}
                type="number"
                step="0.01"
                {...register('amountCollectedRate', {
                  valueAsNumber: true,
                  min: { value: 0.01, message: 'Rate must be positive' }
                })}
                error={errors.amountCollectedRate?.message}
                placeholder="e.g., 50.00"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InAmountSection;
